import { useEffect, useRef, useState } from "react";
import { isSortableOperation } from "@dnd-kit/react/sortable";
import { v4 as uuidv4 } from "uuid";
import { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { getRouteMetrics } from "../../domain/RouteMetricsService";
import {
  isSameSubRoute,
  hasConsecutiveSubRoutes,
} from "../../../../../../helpers/booking/subRoutes";

const createSubRute = (name, lat, lng) => ({
  id: uuidv4(),
  name,
  lat,
  lng,
});

const moveItem = (list, fromIndex, toIndex) => {
  const nextList = [...list];
  const [item] = nextList.splice(fromIndex, 1);
  nextList.splice(toIndex, 0, item);
  return nextList;
};

const getLastSubRute = (subRutes) => subRutes[subRutes.length - 1];

const getDestinationInputValue = (addressDestination, subRutes) =>
  subRutes.length > 1 ? `${subRutes.length} PARADAS...` : addressDestination;

export const useBookingLocations = ({ bookingState }) => {
  const [addressDestination, setAddressDestination] = useState(
    bookingState?.data ? bookingState?.data.destination.name : "",
  );
  const [coordDestination, setCoordDestination] = useState({
    lat: bookingState?.data ? bookingState?.data.destination.lat : null,
    lng: bookingState?.data ? bookingState?.data.destination.lng : null,
  });
  const [subRutes, setSubRutes] = useState(
    bookingState?.data?.subRutes
      ? bookingState.data.subRutes.map((subRoute) => ({
          ...subRoute,
          id: subRoute.id || uuidv4(),
        }))
      : [],
  );
  const [openSubRutesModal, setOpenSubRutesModal] = useState(false);
  const [subRouteSearch, setSubRouteSearch] = useState("");
  const [subRouteError, setSubRouteError] = useState("");
  const [, setSubRouteSelected] = useState(null);
  const [routeMetrics, setRouteMetrics] = useState(null);
  const lastSubRuteDragOperation = useRef("");
  const [addressOrigin, setAddressOrigin] = useState(
    bookingState?.data ? bookingState?.data.origin.name : "",
  );
  const [coordOrigin, setCoordOrigin] = useState({
    lat: bookingState?.data ? bookingState?.data.origin.lat : null,
    lng: bookingState?.data ? bookingState?.data.origin.lng : null,
  });
  const destinationInputValue = getDestinationInputValue(
    addressDestination,
    subRutes,
  );

  const syncDestinationFromSubRutes = (nextSubRutes) => {
    const lastSubRute = getLastSubRute(nextSubRutes);
    if (!lastSubRute) {
      setAddressDestination("");
      setCoordDestination({ lat: null, lng: null });
      return;
    }
    setAddressDestination(lastSubRute.name);
    setCoordDestination({ lat: lastSubRute.lat, lng: lastSubRute.lng });
  };

  const handSelect = async (value) => {
    const result = await geocodeByAddress(value);
    const ll = await getLatLng(result[0]);
    setAddressOrigin(value);
    setCoordOrigin(ll);
  };

  const handleSelectDestination = async (value) => {
    const result = await geocodeByAddress(value);
    const ll = await getLatLng(result[0]);
    setAddressDestination(value);
    setCoordDestination(ll);
    setSubRutes([createSubRute(value, ll.lat, ll.lng)]);
  };

  const handleSelectDestinationPoint = (address, point) => {
    setAddressDestination(address);
    setCoordDestination(point);
    setSubRutes([createSubRute(address, point.lat, point.lng)]);
  };

  const handleOpenDestinationRoutes = () => {
    if (subRutes.length > 0) setOpenSubRutesModal(true);
  };

  const handleCloseSubRutesModal = () => {
    setOpenSubRutesModal(false);
    setSubRouteSearch("");
    setSubRouteError("");
    setSubRouteSelected(null);
  };

  const addSubRoute = (nextSubRoute) => {
    setSubRutes((currentSubRutes) => {
      if (isSameSubRoute(getLastSubRute(currentSubRutes), nextSubRoute)) {
        setSubRouteError("No puedes agregar dos rutas iguales seguidas.");
        return currentSubRutes;
      }
      const nextSubRutes = [...currentSubRutes, nextSubRoute];
      syncDestinationFromSubRutes(nextSubRutes);
      setSubRouteError("");
      return nextSubRutes;
    });
    setSubRouteSearch("");
    setSubRouteSelected(null);
  };

  const handleSelectSubRoute = async (value) => {
    const result = await geocodeByAddress(value);
    const ll = await getLatLng(result[0]);
    const nextSubRoute = createSubRute(value, ll.lat, ll.lng);
    addSubRoute(nextSubRoute);
  };

  const handleSelectSubRoutePoint = (address, point) => {
    const nextSubRoute = createSubRute(address, point.lat, point.lng);
    addSubRoute(nextSubRoute);
  };

  const handleAcceptSubRoute = () => {
    handleCloseSubRutesModal();
  };

  const handleRemoveSubRoute = (id) => {
    setSubRutes((currentSubRutes) => {
      const nextSubRutes = currentSubRutes.filter(
        (subRoute) => subRoute.id !== id,
      );
      syncDestinationFromSubRutes(nextSubRutes);
      return nextSubRutes;
    });
  };

  const reorderSubRutes = (event) => {
    if (event.canceled || !isSortableOperation(event.operation)) return;
    const { source, target } = event.operation;
    if (!target || source.id === target.id) return;
    const operationKey = `${source.id}-${target.id}`;
    if (lastSubRuteDragOperation.current === operationKey) return;
    lastSubRuteDragOperation.current = operationKey;
    setSubRutes((currentSubRutes) => {
      const sourceIndex = currentSubRutes.findIndex(
        (subRoute) => subRoute.id === source.id,
      );
      const targetIndex = currentSubRutes.findIndex(
        (subRoute) => subRoute.id === target.id,
      );
      if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
        return currentSubRutes;
      }
      const nextSubRutes = moveItem(currentSubRutes, sourceIndex, targetIndex);
      if (hasConsecutiveSubRoutes(nextSubRutes)) {
        setSubRouteError("No puedes dejar dos rutas iguales seguidas.");
        return currentSubRutes;
      }
      setSubRouteError("");
      syncDestinationFromSubRutes(nextSubRutes);
      return nextSubRutes;
    });
  };

  const handleSubRutesDragOver = (event) => {
    reorderSubRutes(event);
  };

  const handleSubRutesDragEnd = (event) => {
    reorderSubRutes(event);
    lastSubRuteDragOperation.current = "";
  };

  const resetBookingLocations = () => {
    setAddressDestination("");
    setCoordDestination({ lat: null, lng: null });
    setSubRutes([]);
    setOpenSubRutesModal(false);
    setSubRouteSearch("");
    setSubRouteError("");
    setSubRouteSelected(null);
    setRouteMetrics(null);
    setAddressOrigin("");
    setCoordOrigin({ lat: null, lng: null });
    lastSubRuteDragOperation.current = "";
  };

  useEffect(() => {
    const calculateRouteMetrics = async () => {
      try {
        const metrics = await getRouteMetrics({
          origin: coordOrigin,
          subRutes,
        });

        setRouteMetrics(metrics);
      } catch (error) {
        console.log(error);
        setRouteMetrics(null);
      }
    };

    calculateRouteMetrics();
  }, [coordOrigin, subRutes]);

  return {
    addressDestination,
    setAddressDestination,
    coordDestination,
    setCoordDestination,
    subRutes,
    openSubRutesModal,
    subRouteSearch,
    setSubRouteSearch,
    subRouteError,
    setSubRouteError,
    setSubRouteSelected,
    routeMetrics,
    addressOrigin,
    setAddressOrigin,
    coordOrigin,
    setCoordOrigin,
    destinationInputValue,
    handSelect,
    handleSelectDestination,
    handleSelectDestinationPoint,
    handleOpenDestinationRoutes,
    handleCloseSubRutesModal,
    handleSelectSubRoute,
    handleSelectSubRoutePoint,
    handleAcceptSubRoute,
    handleRemoveSubRoute,
    handleSubRutesDragOver,
    handleSubRutesDragEnd,
    resetBookingLocations,
  };
};
